import clsx from "clsx"
import { FC, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { avatarDefaultSrc } from "@/assets"
import { CurrencyIcon } from "@/helpers/currencyIcon"
import { dayjsServer } from "@/helpers/dayjsServer"
import { ServerDate } from "@/helpers/server-date.class"
import { showToast } from "@/helpers/toast"
import { useMatchConfirm } from "@/hooks/useMatchConfirm"
import { useMoneyEnough } from "@/hooks/useMoneyEnough"
import { useServerTimer } from "@/hooks/useServerTimer"
import { useModal } from "@/modals"
import { useAppDispatch, useAppSelector } from "@/store"
import { CURRENCY } from "@/store/apis/balanceAPI"
import {
  useGetMatchConfirmSessionQuery,
  useGetMyMatchSessionQuery,
  useMatchCancelMutation,
  useMatchConfirmMutation
} from "@/store/apis/matchAPI"
import { useGetPlayerProfileQuery } from "@/store/apis/playerAPI"
import { setFindMatchMode } from "@/store/slices/findMatch"
import { MatchSelectors } from "@/store/slices/match"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import { PlayerStatus } from "@/ui/PlayerStatus"
import styles from "./FindTonMatchModal.module.scss"

const modalType = "findTonMatch"

export const FindTonMatchModal: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { modal } = useModal(modalType)
  const { player, rival, matchStatus } = useMatchConfirm()

  const matchSessionRedux = useAppSelector(MatchSelectors.selectMatchSession())

  const buyInValue = matchSessionRedux?.pot?.buyInValue ?? 0

  const isMoneyEnough = useMoneyEnough(buyInValue, "ton")

  const { minutes, seconds, restart } = useServerTimer({
    expiryTimestamp: new ServerDate(),
    autoStart: false,
    onExpire: () => {
      console.log("expired")
    }
  })

  const { data: playerProfile } = useGetPlayerProfileQuery()
  const { data: matchSessionQuery } = useGetMyMatchSessionQuery(
    {
      playerId: playerProfile?._id ?? ""
    },
    {
      skip: !playerProfile?._id
    }
  )

  const { data: matchConfirmSessionQuery } = useGetMatchConfirmSessionQuery(
    {
      sessionId: matchSessionRedux?.sessionId ?? "",
      playerId: playerProfile?._id ?? ""
    },
    {
      skip: !matchSessionRedux?.sessionId || !playerProfile?._id
    }
  )

  const [matchConfirm, { isLoading: isMatchConfirmLoading }] =
    useMatchConfirmMutation()
  const [cancelMatch, { isLoading: isCancelMatchLoading }] =
    useMatchCancelMutation()

  const Icon = useMemo(() => {
    return CurrencyIcon[matchSessionQuery?.pot?.buyInCurrency ?? CURRENCY.TON]
  }, [matchSessionQuery?.pot?.buyInCurrency])

  const timer = useMemo(() => {
    if (minutes > 0) {
      return `${minutes}${t("minute_short")} ${seconds}${t("second_short")}`
    }

    return `${seconds}${t("second_short")}`
  }, [minutes, seconds])

  const handleConfirm = () => {
    matchConfirm({
      sessionId: matchSessionRedux?.sessionId ?? "",
      accept: true
    }).then((data) => {
      if (!data?.data) {
        showToast.error(t("errors.somethingWentWrong"))
      }
    })
  }

  const handleClose = () => {
    dispatch(setFindMatchMode("label"))
    modal.close()
  }

  const handleCancel = async () => {
    await cancelMatch()
    modal.close()
  }

  const handleDisconnect = () => {
    matchConfirm({
      sessionId: matchSessionRedux?.sessionId ?? "",
      accept: false
    }).then((data) => {
      if (data?.data) {
        modal.close()
      } else {
        showToast.error(t("errors.somethingWentWrong"))
      }
    })
  }

  useEffect(() => {
    if (!matchConfirmSessionQuery) return

    const date = dayjsServer(matchConfirmSessionQuery.expiresAt).toDate()

    restart(date)
  }, [matchConfirmSessionQuery?.expiresAt, restart])

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.FindTonMatchModal)}
      body={{ className: styles.FindTonMatchModal_body }}
      isCanClose={false}
    >
      <Modal.StoneFlexContainer
        className={styles.FindTonMatchModal_container}
        styles={"ton"}
        content={{ className: styles.FindTonMatchModal_content }}
        outside={
          <div className={clsx(styles.FindTonMatchModal_footerButtons)}>
            <Button
              className={clsx(
                styles.FindTonMatchModal_footerButtons_item,
                styles.close
              )}
              styles="green"
              onClick={handleClose}
              disabled={isMatchConfirmLoading || isCancelMatchLoading}
            >
              {t("button.close")}
            </Button>
            {matchStatus !== "WAITING_FOR_OPPONENT" && (
              <Button
                className={clsx(
                  styles.FindTonMatchModal_footerButtons_item,
                  styles.disconnect
                )}
                onClick={handleDisconnect}
                isLoading={isMatchConfirmLoading}
              >
                {t("button.disconnect")}
              </Button>
            )}
            {matchStatus === "WAITING_FOR_OPPONENT" && (
              <Button
                className={clsx(
                  styles.FindTonMatchModal_footerButtons_item,
                  styles.cancel
                )}
                onClick={handleCancel}
                isLoading={isCancelMatchLoading}
              >
                {t("button.cancel")}
              </Button>
            )}
            {player?.status === "PENDING" && (
              <Button
                className={clsx(
                  styles.FindTonMatchModal_footerButtons_item,
                  styles.ready
                )}
                disabled={!isMoneyEnough}
                onClick={handleConfirm}
                isLoading={isMatchConfirmLoading}
              >
                {t("button.ready")} <Icon />
                {buyInValue}
              </Button>
            )}
          </div>
        }
      >
        <OutlinedText className={clsx(styles.FindTonMatchModal_title)}>
          {t("playForTon.findMatchModal.title")}
        </OutlinedText>
        {matchStatus !== "WAITING_FOR_OPPONENT" && (
          <div className={clsx(styles.FindTonMatchModal_timer)}>{timer}</div>
        )}
        <div className={clsx(styles.FindTonMatchModal_players)}>
          <PlayerStatus
            avatar={avatarDefaultSrc}
            style="ton"
            name={t("you.nominative")}
            isReady={player && player?.status === "CONFIRMED"}
          />
          <div className={clsx(styles.FindTonMatchModal_vs)}>
            {t("playForTon.findMatchModal.vs")}
          </div>
          <PlayerStatus
            style="ton"
            avatar={rival && avatarDefaultSrc}
            name={rival && t("rival.nominative")}
            isReady={rival && rival?.status === "CONFIRMED"}
          />
        </div>
        <OutlinedText className={clsx(styles.FindTonMatchModal_text)}>
          {t("playForTon.findMatchModal.text")}
        </OutlinedText>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
